import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue as mode,
  useToast,
  VStack,
} from '@chakra-ui/react';
import LimitationsForm from 'components/LimitationsForm';
import { Field, FieldProps, Form, Formik } from 'formik';
import { getDefaultExpirationDate, getDefaultLimitations } from 'helpers/companyDefaults';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateOrganizationPayload } from 'services/organization';

interface AddCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateOrganizationPayload) => void;
}

const AddCompanyModal: React.FC<AddCompanyModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const toast = useToast();

  const [formData, setFormData] = useState<CreateOrganizationPayload>({
    name: '',
    admin: {
      email: '',
      first_name: '',
      last_name: '',
    },
    limitation: {
      storage_gb: getDefaultLimitations().storage_gb,
      question: getDefaultLimitations().question,
      translation: getDefaultLimitations().translation,
      expiration: null, // Default is unlimited
    },
  });

  // Separate state for form display (keeps GB values)
  const [displayLimitations, setDisplayLimitations] = useState({
    storage_gb: getDefaultLimitations().storage_gb,
    question: getDefaultLimitations().question,
    translation: getDefaultLimitations().translation,
    expiration: null, // Default is unlimited
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [unlimitedFlags, setUnlimitedFlags] = useState({
    storage: false,
    question: false,
    translation: false,
    expiration: true, // Default is unlimited, so toggle should be ON (unlimited)
  });

  const validateCompanyName = useCallback(
    (value: string) => {
      let error;
      if ((value || '').trim().length === 0) {
        error = t('managementSystem.addCompany.companyNameRequired');
      }
      return error;
    },
    [t]
  );

  const filterCompanyName = (value: string) => {
    // Only allow letters, numbers, and underscores
    return value.replace(/[^a-zA-Z0-9_]/g, '');
  };

  const validateEmail = useCallback(
    (value: string) => {
      let error;
      if ((value || '').trim().length === 0) {
        error = t('managementSystem.addCompany.emailRequired');
      } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(value)) {
        error = t('managementSystem.addCompany.emailInvalid');
      }
      return error;
    },
    [t]
  );

  const validateFirstName = useCallback(
    (value: string) => {
      let error;
      if ((value || '').trim().length === 0) {
        error = t('managementSystem.addCompany.firstNameRequired');
      }
      return error;
    },
    [t]
  );

  const validateLastName = useCallback(
    (value: string) => {
      let error;
      if ((value || '').trim().length === 0) {
        error = t('managementSystem.addCompany.lastNameRequired');
      }
      return error;
    },
    [t]
  );

  const handleInputChange = (field: string, value: string | number) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      if (parent === 'admin') {
        setFormData((prev) => ({
          ...prev,
          admin: {
            ...prev.admin,
            [child]: value,
          },
        }));
      } else if (parent === 'limitation') {
        setFormData((prev) => ({
          ...prev,
          limitation: {
            ...prev.limitation,
            [child]: value,
          },
        }));
        // Also update display limitations
        setDisplayLimitations((prev) => ({
          ...prev,
          [child]: value,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleUnlimitedToggle = (field: keyof typeof unlimitedFlags) => {
    setUnlimitedFlags((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));

    // Set value to null when toggle is ON (Unlimited)
    if (!unlimitedFlags[field]) {
      if (field === 'expiration') {
        handleInputChange('limitation.expiration', 'Unlimited');
      } else {
        // Set to null for unlimited
        const limitationField = field === 'storage' ? 'storage_gb' : field;
        setFormData((prev) => ({
          ...prev,
          limitation: {
            ...prev.limitation,
            [limitationField]: null,
          },
        }));
        // Also update display limitations
        setDisplayLimitations((prev) => ({
          ...prev,
          [limitationField]: null,
        }));
      }
    } else {
      // Reset to default values when toggle is OFF (Limited)
      if (field === 'expiration') {
        handleInputChange(
          'limitation.expiration',
          getDefaultExpirationDate().toISOString().split('T')[0]
        );
      } else if (field === 'storage') {
        handleInputChange('limitation.storage_gb', getDefaultLimitations().storage_gb);
      } else if (field === 'question') {
        handleInputChange('limitation.question', getDefaultLimitations().question);
      } else if (field === 'translation') {
        handleInputChange('limitation.translation', getDefaultLimitations().translation);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      admin: {
        email: '',
        first_name: '',
        last_name: '',
      },
      limitation: {
        storage_gb: getDefaultLimitations().storage_gb,
        question: getDefaultLimitations().question,
        translation: getDefaultLimitations().translation,
        expiration: null, // Default is unlimited
      },
    });
    setUnlimitedFlags({
      storage: false,
      question: false,
      translation: false,
      expiration: true, // Default is unlimited
    });
  };

  const handleSubmit = (formikValues: any) => {
    setIsSubmitting(true);

    // Convert storage from GB to Bytes before sending to API
    const apiPayload = {
      name: formikValues.name,
      admin: {
        email: formikValues.email,
        first_name: formikValues.firstName,
        last_name: formikValues.lastName,
      },
      limitation: {
        storage_gb: unlimitedFlags.storage ? null : formData.limitation.storage_gb || 0,
        question: unlimitedFlags.question ? null : formData.limitation.question,
        translation: unlimitedFlags.translation ? null : formData.limitation.translation,
        expiration: unlimitedFlags.expiration ? null : formData.limitation.expiration,
      },
    };

    // Call onSubmit without await - let Redux saga handle the API call
    onSubmit(apiPayload);

    // Don't show toast here - let Redux saga handle success/error
    // Modal will be closed by parent component when success
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('managementSystem.addCompany.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Formik
            initialValues={{
              name: '',
              email: '',
              firstName: '',
              lastName: '',
            }}
            onSubmit={async (values, { setSubmitting, setFieldTouched }) => {
              // Call handleSubmit with Formik values directly
              handleSubmit(values);
              setSubmitting(false);
            }}
            validateOnMount={false}
            validateOnChange={true}
            validateOnBlur={true}
          >
            {({ errors, touched, values, setFieldValue, handleSubmit: formikSubmit }) => (
              <Form id='addCompanyForm' noValidate>
                <VStack spacing={6}>
                  {/* Organization Name */}
                  <Field name='name' validate={validateCompanyName}>
                    {({ field }: FieldProps) => (
                      <FormControl isInvalid={!!(errors.name && touched.name)} isRequired>
                        <FormLabel>{t('managementSystem.addCompany.companyName')}</FormLabel>
                        <Text mb={2} color={mode('gray.600', 'gray.400')} fontSize='xs'>
                          {t('managementSystem.addCompany.companyNameNote')}
                        </Text>
                        <Input
                          {...field}
                          color={mode('black', 'white')}
                          bg={mode('white', 'gray.700')}
                          border='1px solid'
                          borderColor={
                            errors.name && touched.name ? 'red.500' : mode('gray.300', 'gray.500')
                          }
                          _focus={{
                            borderColor:
                              errors.name && touched.name
                                ? 'red.500'
                                : mode('blue.500', 'blue.300'),
                          }}
                          _placeholder={{ color: mode('gray.500', 'gray.400') }}
                          onChange={(e) => {
                            const filteredValue = filterCompanyName(e.target.value);
                            field.onChange(e);
                            setFieldValue('name', filteredValue);
                          }}
                          placeholder={t('managementSystem.addCompany.companyName')}
                        />
                        <Box pos='relative' minH='0'>
                          {errors.name && touched.name && (
                            <FormErrorMessage
                              pos='absolute'
                              top='4px'
                              left='0'
                              m={0}
                              color='red.500'
                              fontSize='sm'
                            >
                              {errors.name}
                            </FormErrorMessage>
                          )}
                        </Box>
                      </FormControl>
                    )}
                  </Field>

                  {/* Admin Information */}
                  <Field name='email' validate={validateEmail}>
                    {({ field }: FieldProps) => (
                      <FormControl isInvalid={!!(errors.email && touched.email)} isRequired>
                        <FormLabel>Admin email</FormLabel>
                        <Input
                          {...field}
                          color={mode('black', 'white')}
                          bg={mode('white', 'gray.700')}
                          border='1px solid'
                          borderColor={
                            errors.email && touched.email ? 'red.500' : mode('gray.300', 'gray.500')
                          }
                          _focus={{
                            borderColor:
                              errors.email && touched.email
                                ? 'red.500'
                                : mode('blue.500', 'blue.300'),
                          }}
                          _placeholder={{ color: mode('gray.500', 'gray.400') }}
                          placeholder='admin@company.com'
                          type='email'
                        />
                        <Box pos='relative' minH='0'>
                          {errors.email && touched.email && (
                            <FormErrorMessage
                              pos='absolute'
                              top='4px'
                              left='0'
                              m={0}
                              color='red.500'
                              fontSize='sm'
                            >
                              {errors.email}
                            </FormErrorMessage>
                          )}
                        </Box>
                      </FormControl>
                    )}
                  </Field>

                  <HStack w='100%' spacing={4}>
                    <Field name='firstName' validate={validateFirstName}>
                      {({ field }: FieldProps) => (
                        <FormControl
                          isInvalid={!!(errors.firstName && touched.firstName)}
                          isRequired
                        >
                          <FormLabel>{t('managementSystem.addCompany.firstName')}</FormLabel>
                          <Input
                            {...field}
                            color={mode('black', 'white')}
                            bg={mode('white', 'gray.700')}
                            border='1px solid'
                            borderColor={
                              errors.firstName && touched.firstName
                                ? 'red.500'
                                : mode('gray.300', 'gray.500')
                            }
                            _focus={{
                              borderColor:
                                errors.firstName && touched.firstName
                                  ? 'red.500'
                                  : mode('blue.500', 'blue.300'),
                            }}
                            _placeholder={{ color: mode('gray.500', 'gray.400') }}
                            placeholder={t('managementSystem.addCompany.firstName')}
                          />
                          <Box pos='relative' minH='0'>
                            {errors.firstName && touched.firstName && (
                              <FormErrorMessage
                                pos='absolute'
                                top='4px'
                                left='0'
                                m={0}
                                color='red.500'
                                fontSize='sm'
                              >
                                {errors.firstName}
                              </FormErrorMessage>
                            )}
                          </Box>
                        </FormControl>
                      )}
                    </Field>

                    <Field name='lastName' validate={validateLastName}>
                      {({ field }: FieldProps) => (
                        <FormControl isInvalid={!!(errors.lastName && touched.lastName)} isRequired>
                          <FormLabel>{t('managementSystem.addCompany.lastName')}</FormLabel>
                          <Input
                            {...field}
                            color={mode('black', 'white')}
                            bg={mode('white', 'gray.700')}
                            border='1px solid'
                            borderColor={
                              errors.lastName && touched.lastName
                                ? 'red.500'
                                : mode('gray.300', 'gray.500')
                            }
                            _focus={{
                              borderColor:
                                errors.lastName && touched.lastName
                                  ? 'red.500'
                                  : mode('blue.500', 'blue.300'),
                            }}
                            _placeholder={{ color: mode('gray.500', 'gray.400') }}
                            placeholder={t('managementSystem.addCompany.lastName')}
                          />
                          <Box pos='relative' minH='0'>
                            {errors.lastName && touched.lastName && (
                              <FormErrorMessage
                                pos='absolute'
                                top='4px'
                                left='0'
                                m={0}
                                color='red.500'
                                fontSize='sm'
                              >
                                {errors.lastName}
                              </FormErrorMessage>
                            )}
                          </Box>
                        </FormControl>
                      )}
                    </Field>
                  </HStack>

                  <LimitationsForm
                    limitations={formData.limitation}
                    unlimitedFlags={unlimitedFlags}
                    onToggleUnlimited={handleUnlimitedToggle}
                    onInputChange={handleInputChange}
                    isEdit={false}
                  />
                </VStack>

                <ModalFooter justifyContent='flex-end' mt={6} px={0}>
                  <Button mr={3} onClick={handleClose} variant='cancel'>
                    {t('managementSystem.addCompany.cancel')}
                  </Button>
                  <Button
                    color='white'
                    bg='main_color'
                    _hover={{ bg: 'main_color', opacity: 0.8 }}
                    form='addCompanyForm'
                    isDisabled={isSubmitting}
                    isLoading={isSubmitting}
                    loadingText='Creating...'
                    type='submit'
                  >
                    {t('managementSystem.addCompany.add')}
                  </Button>
                </ModalFooter>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddCompanyModal;
